import { Command, Help, type Option } from 'commander';

/**
 * Names (in order) of commands that go in the "basic" group.
 */
export const basicCommands = ['start', 'init', 'bundle', 'link', 'unlink'];

/**
 * Help group names for the primary commands.
 * (Sub-commands will use the default "Commands:" group.)
 *
 * The ordering of entries is used to determine the order of sections in help output.
 */
const commandGroups = {
  /** Main commands the user cares about */
  basic: 'Basic commands:',
  /** Lower-level or lesser-used commands (default value) */
  other: 'Other commands:',
};

/**
 * Help group names for options (pass to `option.helpGroup()`).
 *
 * The ordering of entries is used to determine the order of sections in help output.
 */
export const optionGroups = {
  default: 'Options:',
  /** Options related to the logging output */
  logging: 'Logging options:',
  /** Non-logging options shared between all/most commands */
  common: 'Common options:',
};

export class CloudpackHelp extends Help {
  private _isTopLevel: boolean;

  constructor(params: { isTopLevel: boolean }) {
    super();
    // Sort commands by name
    this.sortSubcommands = true;
    this._isTopLevel = params.isTopLevel;
  }

  override visibleCommands(cmd: Command): Command[] {
    const commands = super.visibleCommands(cmd);
    const helpIndex = commands.findIndex((c) => c.name() === 'help');
    if (helpIndex >= 0) {
      // Move the help command to the end of the list
      const helpCommand = commands.splice(helpIndex, 1)[0];
      commands.push(helpCommand);
    }
    return commands;
  }

  override visibleOptions(cmd: Command): Option[] {
    const options = super.visibleOptions(cmd);

    if (!this._isTopLevel) {
      // Remove --version from help except at the top level (it's not very useful)
      return options.filter((option) => option.name() !== 'version');
    }

    return options;
  }

  /**
   * Group options or child commands for help.
   */
  override groupItems<T extends Command | Option>(
    unsortedItems: T[],
    visibleItems: T[],
    getGroup: (item: T) => string,
  ): Map<string, T[]> {
    const isCommands = (unsortedItems[0] || visibleItems[0]) instanceof Command;
    let groups: Map<string, T[]>;
    if (isCommands && this._isTopLevel) {
      groups = super.groupItems(unsortedItems, visibleItems, (item) =>
        basicCommands.includes(item.name()) ? commandGroups.basic : commandGroups.other,
      );
      const basicGroup = groups.get(commandGroups.basic);
      if (basicGroup) {
        // Force the basic commands to use the order from the array
        basicGroup.sort((a, b) => basicCommands.indexOf(a.name()) - basicCommands.indexOf(b.name()));
      }
    } else {
      groups = super.groupItems(unsortedItems, visibleItems, (item) =>
        item.name() === 'help' ? optionGroups.common : getGroup(item),
      );
    }

    if (groups.size < 2) {
      return groups;
    }

    // Sort the groups: anything not listed goes first, followed by groups in the listed order.
    const orderedGroupNames = Object.values(isCommands ? commandGroups : optionGroups);
    const unlistedGroups = [...groups.keys()].filter((g) => !orderedGroupNames.includes(g));

    const sortedGroups = new Map<string, T[]>();
    for (const groupName of [...unlistedGroups, ...orderedGroupNames]) {
      const group = groups.get(groupName);
      if (group) {
        sortedGroups.set(groupName, group);
      }
    }

    return sortedGroups;
  }
}
